/**
 * CreateApiKeyDialog — shared "mint a gateway API key" dialog.
 *
 * Used by the Settings ApiKeysSection and the model page's API access
 * buttons. Form state/validation/mutation live in useCreateApiKeyForm; the
 * one-time ApiKeyCreateResponse (containing the raw secret) goes to the
 * caller via `onCreated` — the caller decides how to reveal it.
 */

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ApiKeyCreateResponse } from '@/hooks/useApiKeys'
import { useCreateApiKeyForm } from '@/hooks/useCreateApiKeyForm'

interface CreateApiKeyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (key: ApiKeyCreateResponse) => void
}

export function CreateApiKeyDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateApiKeyDialogProps): React.JSX.Element {
  const form = useCreateApiKeyForm((key) => {
    onCreated(key)
    onOpenChange(false)
  })

  function close(): void {
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) form.reset()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create API key</DialogTitle>
          <DialogDescription>
            The secret is shown once, right after the key is created. Name the
            key after where it will live.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); form.submit() }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="key-name">Name</Label>
            <Input
              id="key-name"
              value={form.name}
              onChange={(e) => { form.setName(e.target.value); form.clearError() }}
              placeholder="Production gateway"
              autoFocus
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="key-expiry">Expires in (days)</Label>
            <Input
              id="key-expiry"
              type="number"
              min={1}
              value={form.expiresInDays}
              onChange={(e) => { form.setExpiresInDays(e.target.value); form.clearError() }}
              placeholder="Optional. Leave blank for no expiry"
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="key-data-api"
              className="mt-0.5"
              checked={form.allowDataApi}
              onCheckedChange={(checked) => { form.setAllowDataApi(checked === true) }}
            />
            <div className="space-y-1">
              <Label htmlFor="key-data-api">Also allow data API access</Label>
              <p className="text-xs text-muted-foreground">
                The key can then read team data through the data API. Keys that
                only send inference traffic do not need this.
              </p>
            </div>
          </div>
          {form.error && <p className="text-sm text-destructive">{form.error}</p>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={close}
              disabled={form.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.isPending || !form.name.trim()}>
              {form.isPending ? 'Creating...' : 'Create key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
